/**
 * Small presentational widgets used by the main Dashboard page.
 *
 * Each component is self-contained (no external state/hooks other than what's
 * needed). They were previously inlined at the bottom of `pages/Dashboard.jsx`
 * — extracted here to keep the orchestrator page <600 lines and each concern
 * in a focused file.
 */
import React, { useState } from 'react';
import {
  AlertTriangle, ArrowRight, CreditCard, FileBarChart, RefreshCw,
  Sparkles, Zap, Banknote, ShieldAlert,
} from 'lucide-react';

const fmt = (n) => new Intl.NumberFormat('en-IN', {
  style: 'currency', currency: 'INR', minimumFractionDigits: 2, maximumFractionDigits: 2,
}).format(n || 0);

// ─── Grievance / Issue Notice widget ────────────────────────────────────
export function GrievanceWidget({ items, total, onOpen }) {
  const sevColor = {
    high:   'bg-rose-500/15 text-rose-300 border-rose-500/40',
    medium: 'bg-amber-500/15 text-amber-300 border-amber-500/40',
    low:    'bg-zinc-700/40 text-zinc-300 border-zinc-700',
  };
  return (
    <div data-testid="grievance-widget" className="bg-gradient-to-br from-amber-500/10 to-rose-500/10 border border-amber-500/30 rounded-xl p-5">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div>
          <h3 className="font-heading font-semibold text-zinc-50 text-sm flex items-center gap-2">
            <AlertTriangle size={14} className="text-amber-400" /> Grievances / Issue Notices
          </h3>
          <p className="text-zinc-400 text-[11px] mt-0.5">{total} active issue{total === 1 ? '' : 's'} the AI is tracking. Resolve or update inside Grievance Notices.</p>
        </div>
        <button onClick={onOpen} data-testid="open-grievance-btn"
          className="flex items-center gap-1 text-xs bg-amber-500/15 hover:bg-amber-500/25 text-amber-200 border border-amber-700/50 px-3 py-1.5 rounded-lg font-semibold">
          Open all <ArrowRight size={12} />
        </button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        {items.map(g => (
          <button key={g.id} onClick={onOpen} data-testid={`grievance-item-${g.id}`}
            className="text-left bg-zinc-950/50 hover:bg-zinc-900 border border-zinc-800 rounded-lg p-3 transition-colors">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <span className={`text-[9px] uppercase px-1.5 py-0.5 rounded border ${sevColor[g.severity] || sevColor.low}`}>{g.severity || 'low'}</span>
              <span className="text-[10px] text-zinc-500">{(g.linked_entity_type || '').toUpperCase()}</span>
              <span className="text-[10px] text-zinc-300 font-medium truncate">{g.linked_entity_name || '—'}</span>
              {(g.attachments || []).length > 0 && (
                <span className="text-[9px] text-amber-400 ml-auto flex items-center gap-0.5">📎 {g.attachments.length}</span>
              )}
            </div>
            <div className="text-zinc-200 text-xs line-clamp-2">{g.summary || g.text}</div>
            {g.suggested_solution && (
              <div className="text-emerald-300 text-[10px] mt-1 line-clamp-1">→ {g.suggested_solution}</div>
            )}
          </button>
        ))}
      </div>
    </div>
  );
}

// ─── Tip of the day widget ─────────────────────────────────────────────
export function TipOfDayWidget({ tip }) {
  if (!tip) return null;
  return (
    <div data-testid="tip-of-day-widget" className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 flex items-start gap-3 order-last">
      <div className="w-8 h-8 rounded-full bg-amber-500/15 border border-amber-500/30 flex items-center justify-center text-amber-300 flex-shrink-0">
        <Sparkles size={14} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-[10px] uppercase tracking-wider text-zinc-500">Lesson of the day · {tip.index + 1}/{tip.total}</div>
        <div className="text-zinc-100 font-semibold text-sm">{tip.title}</div>
        <div className="text-zinc-400 text-xs mt-0.5">{tip.body}</div>
      </div>
    </div>
  );
}

// ─── AI Feasibility Risks widget ───────────────────────────────────────
export function FeasibilityRisksWidget({ data, onOpen, onRefresh }) {
  const sev = {
    critical: { box: 'bg-red-500/10 border-red-500/40', text: 'text-red-300' },
    high:     { box: 'bg-amber-500/10 border-amber-500/40', text: 'text-amber-300' },
    medium:   { box: 'bg-sky-500/10 border-sky-500/40', text: 'text-sky-300' },
    ok:       { box: 'bg-emerald-500/10 border-emerald-500/40', text: 'text-emerald-300' },
  };
  return (
    <div data-testid="feasibility-risks-widget" className="bg-gradient-to-br from-amber-500/5 to-red-500/5 border border-amber-500/30 rounded-xl p-5">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div>
          <h3 className="font-heading font-semibold text-zinc-50 text-sm flex items-center gap-2">
            <Zap size={14} className="text-amber-400" /> AI Feasibility · Top Blockers (next {data.horizon_days} days)
          </h3>
          <p className="text-zinc-500 text-[11px] mt-0.5">
            Scanned {data.scanned_orders} open orders · <span className="text-amber-300 font-semibold">{data.blocked_orders}</span> at risk.
            Showing top {data.top.length}.
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={onRefresh} data-testid="feasibility-refresh"
            className="flex items-center gap-1 text-[11px] px-2 py-1 rounded-md bg-zinc-800 hover:bg-zinc-700 text-zinc-300 border border-zinc-700">
            <RefreshCw size={11} /> Re-run
          </button>
          <button onClick={onOpen} data-testid="feasibility-open-sales"
            className="flex items-center gap-1 text-xs bg-amber-500/15 hover:bg-amber-500/25 text-amber-200 border border-amber-700/50 px-3 py-1.5 rounded-lg font-semibold">
            Open sales <ArrowRight size={12} />
          </button>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {data.top.map((b) => {
          const s = sev[b.severity] || sev.medium;
          return (
            <div key={b.order_id} data-testid={`feasibility-card-${b.order_id}`} className={`rounded-lg border ${s.box} p-3`}>
              <div className="flex items-center justify-between gap-2 mb-1">
                <span className={`text-[10px] uppercase tracking-wider ${s.text} font-bold`}>{b.severity}</span>
                {b.days_to_due != null && (
                  <span className="text-[10px] text-zinc-400">
                    due in <span className="text-zinc-100 font-mono">{b.days_to_due}d</span>
                  </span>
                )}
              </div>
              <div className="text-zinc-100 text-sm font-semibold truncate">{b.voucher_no}</div>
              <div className="text-zinc-400 text-[11px] truncate mb-2">{b.client}</div>
              <div className="space-y-1 mb-2">
                {b.shortages.slice(0, 3).map((sh, i) => (
                  <div key={i} className="text-[11px] text-zinc-300 leading-snug">
                    <span className="text-zinc-400">{sh.material_name}</span>
                    {' · '}
                    short <span className="text-red-300 font-mono">{sh.short} {sh.unit}</span>
                    {sh.in_transit_qty > 0 && <> · 🚚 in-transit <span className="text-amber-300 font-mono">{sh.in_transit_qty}</span>
                      {sh.eta_days != null && <> · ETA {sh.eta_days}d</>}</>}
                  </div>
                ))}
                {b.shortages.length > 3 && <div className="text-[10px] text-zinc-500">+ {b.shortages.length - 3} more</div>}
              </div>
              <div className="text-[11px] text-zinc-200 bg-zinc-950/40 border border-zinc-800 rounded p-1.5">{b.recommended_action}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Collection Cockpit widget (debtor side mirror of payment reminders) ─
//
// Visible to admin/owner by default. Other roles need `widget_collection_cockpit`
// explicitly toggled ON from /admin/users → Permissions (iter201).
export function CollectionCockpitWidget({ data, onOpen }) {
  const { overdue, pay_today, upcoming_7d, grand_total_open, risk_summary } = data;
  const topClubs = [...overdue.clubs, ...pay_today.clubs].slice(0, 5);
  const alertCount = risk_summary?.alert_count || 0;
  const watchCount = risk_summary?.watch_count || 0;
  return (
    <div className="bg-gradient-to-br from-emerald-900/20 to-amber-900/10 border border-emerald-700/40 rounded-2xl p-5" data-testid="collection-cockpit-widget">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <h3 className="font-heading font-semibold text-zinc-50 text-sm flex items-center gap-2">
            <Banknote size={14} className="text-emerald-400" /> Collection Cockpit
            {overdue.count > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-red-900/60 text-red-200">{overdue.count} OVERDUE</span>}
            {alertCount > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-rose-900/60 text-rose-200 inline-flex items-center gap-0.5"><ShieldAlert size={10} /> {alertCount} ALERT</span>}
          </h3>
          <p className="text-zinc-400 text-[11px] mt-0.5">
            Open receivables: <span className="text-emerald-300 font-mono font-semibold">{fmt(grand_total_open)}</span> · clubbed by client · {watchCount} watch
          </p>
        </div>
        <button onClick={onOpen} data-testid="open-collection-cockpit"
          className="flex items-center gap-1 text-xs bg-emerald-500/15 hover:bg-emerald-500/25 text-emerald-200 border border-emerald-700/50 px-3 py-1.5 rounded-lg font-semibold">
          Open Full View <ArrowRight size={12} />
        </button>
      </div>
      <div className="grid grid-cols-3 gap-3 mb-4">
        <div className="bg-red-900/30 border border-red-700/40 rounded-lg p-3" data-testid="collection-overdue">
          <div className="text-[10px] text-red-300 uppercase tracking-wider">Overdue</div>
          <div className="font-mono text-xl text-zinc-50 font-bold">{fmt(overdue.total)}</div>
          <div className="text-[10px] text-zinc-400 mt-0.5">{overdue.count} client{overdue.count !== 1 ? 's' : ''}</div>
        </div>
        <div className="bg-amber-900/30 border border-amber-700/40 rounded-lg p-3" data-testid="collection-today">
          <div className="text-[10px] text-amber-300 uppercase tracking-wider">Due Today</div>
          <div className="font-mono text-xl text-zinc-50 font-bold">{fmt(pay_today.total)}</div>
          <div className="text-[10px] text-zinc-400 mt-0.5">{pay_today.count} client{pay_today.count !== 1 ? 's' : ''}</div>
        </div>
        <div className="bg-emerald-900/30 border border-emerald-700/40 rounded-lg p-3" data-testid="collection-upcoming">
          <div className="text-[10px] text-emerald-300 uppercase tracking-wider">Next 7 days</div>
          <div className="font-mono text-xl text-zinc-50 font-bold">{fmt(upcoming_7d.total)}</div>
          <div className="text-[10px] text-zinc-400 mt-0.5">{upcoming_7d.count} client{upcoming_7d.count !== 1 ? 's' : ''}</div>
        </div>
      </div>
      {topClubs.length > 0 && (
        <div className="bg-zinc-950/40 rounded-lg p-3 space-y-1.5">
          <div className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1">Top priority customers</div>
          {topClubs.map((c) => (
            <div key={`coll-${c.client_id || c.client}-${c.from_date || ''}`} className="flex items-center justify-between text-xs">
              <div className="flex items-center gap-1.5 truncate">
                {c.urgent && <span className="text-red-400">🔴</span>}
                <span className="text-zinc-200 truncate">{c.client}</span>
                <span className="text-zinc-500">· {c.count} invoice{c.count > 1 ? 's' : ''} · due {c.from_date}</span>
              </div>
              <span className="text-zinc-100 font-mono font-semibold whitespace-nowrap ml-2">{fmt(c.total)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Payment Reminders widget ─────────────────────────────────────────
export function PaymentRemindersWidget({ data, onOpen }) {
  const { overdue, pay_today, upcoming_7d, grand_total_due_7d } = data;
  const topClubs = [...overdue.clubs, ...pay_today.clubs].slice(0, 5);
  return (
    <div className="bg-gradient-to-br from-red-900/20 to-amber-900/10 border border-red-700/40 rounded-2xl p-5" data-testid="payment-reminders-widget">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <h3 className="font-heading font-semibold text-zinc-50 text-sm flex items-center gap-2">
            <CreditCard size={14} className="text-red-400" /> Vendor Payments Due
            {overdue.count > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-red-900/60 text-red-200">{overdue.count} OVERDUE</span>}
          </h3>
          <p className="text-zinc-400 text-[11px] mt-0.5">
            Next 7 days total: <span className="text-amber-300 font-mono font-semibold">{fmt(grand_total_due_7d)}</span> · clubbed by vendor (±3 day window)
          </p>
        </div>
        <button onClick={onOpen} data-testid="open-payment-reminders"
          className="flex items-center gap-1 text-xs bg-red-500/15 hover:bg-red-500/25 text-red-200 border border-red-700/50 px-3 py-1.5 rounded-lg font-semibold">
          Open Full View <ArrowRight size={12} />
        </button>
      </div>
      <div className="grid grid-cols-3 gap-3 mb-4">
        <div className="bg-red-900/30 border border-red-700/40 rounded-lg p-3" data-testid="payrem-overdue">
          <div className="text-[10px] text-red-300 uppercase tracking-wider">Overdue</div>
          <div className="font-mono text-xl text-zinc-50 font-bold">{fmt(overdue.total)}</div>
          <div className="text-[10px] text-zinc-400 mt-0.5">{overdue.count} vendor{overdue.count !== 1 ? 's' : ''}</div>
        </div>
        <div className="bg-amber-900/30 border border-amber-700/40 rounded-lg p-3" data-testid="payrem-today">
          <div className="text-[10px] text-amber-300 uppercase tracking-wider">Pay Today</div>
          <div className="font-mono text-xl text-zinc-50 font-bold">{fmt(pay_today.total)}</div>
          <div className="text-[10px] text-zinc-400 mt-0.5">{pay_today.count} vendor{pay_today.count !== 1 ? 's' : ''}</div>
        </div>
        <div className="bg-blue-900/30 border border-blue-700/40 rounded-lg p-3" data-testid="payrem-upcoming">
          <div className="text-[10px] text-blue-300 uppercase tracking-wider">Next 7 days</div>
          <div className="font-mono text-xl text-zinc-50 font-bold">{fmt(upcoming_7d.total)}</div>
          <div className="text-[10px] text-zinc-400 mt-0.5">{upcoming_7d.count} vendor{upcoming_7d.count !== 1 ? 's' : ''}</div>
        </div>
      </div>
      {topClubs.length > 0 && (
        <div className="bg-zinc-950/40 rounded-lg p-3 space-y-1.5">
          <div className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1">Top priority vendors</div>
          {topClubs.map((c, i) => (
            <div key={i} className="flex items-center justify-between text-xs">
              <div className="flex items-center gap-1.5 truncate">
                {c.urgent && <span className="text-red-400">🔴</span>}
                <span className="text-zinc-200 truncate">{c.vendor}</span>
                <span className="text-zinc-500">· {c.count} bill{c.count > 1 ? 's' : ''} · due {c.from_date}</span>
              </div>
              <span className="text-zinc-100 font-mono font-semibold whitespace-nowrap ml-2">{fmt(c.total)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Stock Debts widget ────────────────────────────────────────────────
export function StockDebtsWidget({ data, onOpen }) {
  return (
    <div className="bg-red-900/15 border border-red-700/40 rounded-2xl p-4" data-testid="stock-debts-widget">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div>
          <div className="text-red-300 font-semibold text-sm flex items-center gap-1.5">
            <AlertTriangle size={14} /> Stock Debts ({data.total_debts} pending across {data.total_products_owed} product{data.total_products_owed !== 1 ? 's' : ''})
          </div>
          <div className="text-zinc-400 text-[11px] mt-0.5">Auto-fulfilled when you produce these products next.</div>
        </div>
        <button onClick={onOpen} data-testid="open-stock-debts"
          className="flex items-center gap-1 text-xs bg-red-500/20 hover:bg-red-500/30 text-red-200 border border-red-700/50 px-3 py-1.5 rounded-lg font-semibold">
          View all <ArrowRight size={12} />
        </button>
      </div>
      <div className="grid md:grid-cols-2 gap-2">
        {data.by_product.slice(0, 4).map((p, i) => (
          <div key={i} className="bg-zinc-950/40 rounded-lg p-2 flex items-center justify-between text-[12px]">
            <span className="text-zinc-200 truncate">{p.product_name}</span>
            <span className="text-red-300 font-mono whitespace-nowrap">-{p.qty_outstanding} {p.unit} · {p.count} debt{p.count > 1 ? 's' : ''}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Cost Variance widget ──────────────────────────────────────────────
export function CostVarianceWidget({ data, onOpen }) {
  const kpi = data.kpi || {};
  const flags = data.top_flags || [];
  return (
    <div className="bg-gradient-to-br from-amber-900/20 to-orange-900/10 border border-amber-700/40 rounded-2xl p-5" data-testid="cost-variance-widget">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
        <div>
          <h3 className="font-heading font-semibold text-zinc-50 text-sm flex items-center gap-2">
            <FileBarChart size={14} className="text-amber-400" /> Cost Variance Alert
            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-900/60 text-amber-200">
              {kpi.total_flags} flag{kpi.total_flags !== 1 ? 's' : ''}
            </span>
          </h3>
          <p className="text-zinc-400 text-[11px] mt-0.5">
            {data.prev_month} → {data.this_month} · {kpi.total_variance_pct !== null && kpi.total_variance_pct !== undefined
              ? `overall ${kpi.total_variance_pct > 0 ? '+' : ''}${kpi.total_variance_pct}%`
              : 'this month vs last'}
          </p>
        </div>
        <button onClick={onOpen} data-testid="open-cost-variance"
          className="flex items-center gap-1 text-xs bg-amber-500/15 hover:bg-amber-500/25 text-amber-200 border border-amber-700/50 px-3 py-1.5 rounded-lg font-semibold">
          Full Report <ArrowRight size={12} />
        </button>
      </div>
      {flags.length > 0 ? (
        <div className="bg-zinc-950/40 rounded-lg p-3 space-y-1.5">
          <div className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1">Top variance flags</div>
          {flags.map((f, i) => (
            <div key={i} className="flex items-center justify-between text-xs">
              <span className="text-zinc-200 truncate">{f.category || f.name}</span>
              <span className={`font-mono font-semibold whitespace-nowrap ml-2 ${f.pct_change > 0 ? 'text-red-300' : 'text-green-300'}`}>
                {f.pct_change > 0 ? '+' : ''}{f.pct_change}%
              </span>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

// ─── Expiry Alerts widget ──────────────────────────────────────────────
export function ExpiryAlertsWidget({ data, onPrioritize }) {
  const [openBucket, setOpenBucket] = useState(null);
  const tiles = [
    { key: 'expired',    label: 'Expired',      color: 'text-red-300',    bg: 'bg-red-500/15 border-red-500/30' },
    { key: 'within_30',  label: 'Next 30 days', color: 'text-orange-300', bg: 'bg-orange-500/15 border-orange-500/30' },
    { key: 'within_60',  label: 'Next 60 days', color: 'text-amber-300',  bg: 'bg-amber-500/15 border-amber-500/30' },
    { key: 'within_90',  label: 'Next 90 days', color: 'text-yellow-300', bg: 'bg-yellow-500/15 border-yellow-500/30' },
  ];
  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5" data-testid="expiry-alerts-widget">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div>
          <h3 className="font-heading font-semibold text-zinc-50 text-sm flex items-center gap-2">
            <AlertTriangle size={14} className="text-red-400" /> Expiry Alerts
          </h3>
          <p className="text-zinc-500 text-[11px] mt-0.5">
            Raw-material lots nearing expiry · click a tile to expand &amp; mark for <span className="text-amber-400 font-semibold">FIFO priority</span>
          </p>
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {tiles.map(t => {
          const count = data.counts[t.key] || 0;
          return (
            <button key={t.key}
              onClick={() => setOpenBucket(openBucket === t.key ? null : t.key)}
              data-testid={`expiry-tile-${t.key}`}
              disabled={count === 0}
              className={`p-3 rounded-xl border text-left transition-all ${t.bg} ${count > 0 ? 'hover:opacity-90' : 'opacity-40 cursor-not-allowed'} ${openBucket === t.key ? 'ring-1 ring-amber-500/60' : ''}`}>
              <div className="text-zinc-400 text-[10px] uppercase tracking-wider">{t.label}</div>
              <div className={`font-mono font-bold text-2xl mt-1 ${t.color}`}>{count}</div>
              <div className="text-zinc-600 text-[10px] mt-0.5">{count === 1 ? 'lot' : 'lots'}</div>
            </button>
          );
        })}
      </div>
      {openBucket && data.buckets[openBucket]?.length > 0 && (
        <div className="mt-4 border-t border-zinc-800 pt-3" data-testid={`expiry-bucket-${openBucket}`}>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-zinc-800">
                  {['Lot', 'Material', 'Vendor', 'Qty', 'Expiry', 'Days', ''].map(h => (
                    <th key={h} className="text-left text-zinc-500 text-[10px] font-medium px-3 py-2 uppercase tracking-wider">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.buckets[openBucket].map(lot => (
                  <tr key={lot.lot_id} className="border-b border-zinc-800/50" data-testid={`expiry-lot-${lot.lot_id}`}>
                    <td className="px-3 py-2 text-amber-300 font-mono text-[11px] font-semibold">{lot.lot_no}</td>
                    <td className="px-3 py-2 text-zinc-100 text-xs">{lot.material_name}</td>
                    <td className="px-3 py-2 text-zinc-500 text-[11px]">{lot.vendor_name || '—'}</td>
                    <td className="px-3 py-2 text-zinc-300 font-mono text-[11px]">{lot.qty_remaining} {lot.unit}</td>
                    <td className="px-3 py-2 text-zinc-400 font-mono text-[11px]">{lot.expiry_date}</td>
                    <td className={`px-3 py-2 font-mono text-[11px] font-semibold ${lot.days_left < 0 ? 'text-red-400' : lot.days_left <= 30 ? 'text-orange-400' : 'text-amber-400'}`}>
                      {lot.days_left < 0 ? `${Math.abs(lot.days_left)}d ago` : `in ${lot.days_left}d`}
                    </td>
                    <td className="px-3 py-2">
                      <button onClick={() => onPrioritize(lot.lot_id)}
                        data-testid={`prioritize-${lot.lot_id}`}
                        className="flex items-center gap-1 px-2.5 py-1 bg-amber-500 hover:bg-amber-400 text-black rounded-lg text-[10px] font-semibold whitespace-nowrap">
                        Use First in Production
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
